import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Download, Filter, Calendar, BookOpen, TrendingDown, X, CheckSquare, Square, Plus, Eye, FileText, Settings, Shield, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';
import { useActiveCompanyId } from '../components/CompanySwitcher';

/**
 * Banner that exposes the per-tenant ``expense_post_mode`` policy.
 *
 * • ``auto`` (default) — bank-paid expenses auto-post to the GL when
 *   they clear the workflow gate threshold.
 * • ``report_only`` — bank-paid expenses NEVER auto-post; they must be
 *   bundled into an Expense Report → Approval → only then a single
 *   aggregated journal is written.
 *
 * Petty-cash expenses are exempt from both modes (the cash has already
 * left the drawer; the journal must record that immediately).
 */
function PostModeBanner() {
  const [mode, setMode] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeIdFromCtx] = useActiveCompanyId();

  // Resolve the company id from ALL known sources in priority order:
  //   1. localStorage `active_company_id` (set by CompanySwitcher)
  //   2. /auth/me → default_company_id
  //   3. /auth/me → first entry in companies[]
  //   4. /companies → first row (single-tenant deployments)
  const resolveCompanyId = async () => {
    try {
      if (activeIdFromCtx) return activeIdFromCtx;
      try {
        const ls = localStorage.getItem('active_company_id');
        if (ls) return ls;
      } catch { /* ignore */ }
      const me = await axiosInstance.get('/auth/me').catch(() => null);
      const c1 = me?.data?.default_company_id
              || me?.data?.active_company_id
              || me?.data?.companies?.[0]?.id;
      if (c1) return c1;
      const list = await axiosInstance.get('/companies').catch(() => null);
      const c2 = list?.data?.[0]?.id;
      if (c2) return c2;
    } catch { /* ignore */ }
    return null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cid = await resolveCompanyId();
      if (cancelled) return;
      setCompanyId(cid);
      if (!cid) {
        // Still show banner with default so admin sees the feature exists.
        setMode('auto');
        return;
      }
      try {
        const r = await axiosInstance.get(`/companies/${cid}/expense-post-mode`);
        if (!cancelled) setMode(r.data?.expense_post_mode || 'auto');
      } catch {
        if (!cancelled) setMode('auto');
      }
    })();
    return () => { cancelled = true; };
  }, [activeIdFromCtx]);

  const flip = async () => {
    if (saving) return;
    const next = mode === 'report_only' ? 'auto' : 'report_only';
    if (!window.confirm(
      next === 'report_only'
        ? 'تفعيل الوضع المُقيَّد: لن تترحَّل المصروفات تلقائياً إلى اليومية. يجب تجميعها في Expense Report والموافقة عليه أولاً. متابعة؟'
        : 'الرجوع إلى الترحيل التلقائي: المصروفات الصغيرة (≤5K SAR) ستترحَّل فوراً عند إضافتها. متابعة؟',
    )) return;
    setSaving(true);
    try {
      // Re-resolve at action time — covers the case where the user
      // landed on the page before CompanySwitcher hydrated.
      const cid = companyId || (await resolveCompanyId());
      if (!cid) {
        toast.error('لا توجد شركة فعّالة. اختر الشركة من المُبدِّل في أعلى الشاشة، ثم أعد المحاولة.');
        return;
      }
      if (cid !== companyId) setCompanyId(cid);
      const r = await axiosInstance.put(
        `/companies/${cid}/expense-post-mode`, { mode: next },
      );
      setMode(r?.data?.expense_post_mode || next);
      toast.success(next === 'report_only'
        ? '✅ تم تفعيل وضع Expense Report الإلزامي'
        : '✅ تم تفعيل الترحيل التلقائي');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
                : detail?.message
                || e?.message
                || 'فشل التبديل';
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (mode === null) return null;
  const reportOnly = mode === 'report_only';
  return (
    <div data-testid="post-mode-banner"
      className={`mb-4 rounded-lg border px-4 py-3 flex items-center justify-between ${
        reportOnly ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
      }`}>
      <div className="flex items-start gap-2.5">
        <Shield className={`mt-0.5 h-4 w-4 ${reportOnly ? 'text-emerald-600' : 'text-amber-600'}`} />
        <div className="text-xs">
          <div className={`font-bold ${reportOnly ? 'text-emerald-800' : 'text-amber-900'}`}>
            {reportOnly
              ? 'سياسة الترحيل: Report-Gated (مُفعَّلة)'
              : 'سياسة الترحيل: ترحيل تلقائي (افتراضية)'}
          </div>
          <div className={`mt-0.5 ${reportOnly ? 'text-emerald-700' : 'text-amber-800'}`}>
            {reportOnly
              ? 'المصروفات البنكية لا تترحَّل إلى اليومية إلا بعد تحويلها لـ Expense Report وموافقة الإدارة. مصروفات النثرية تُرحَّل فوراً.'
              : 'المصروفات الصغيرة (≤ 5,000 ريال) تترحَّل تلقائياً عند الإضافة. لتغيير ذلك، فعّل الوضع المُقيَّد.'}
          </div>
        </div>
      </div>
      <button data-testid="post-mode-toggle" onClick={flip} disabled={saving}
        className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition disabled:opacity-50 ${
          reportOnly
            ? 'border-amber-300 bg-white text-amber-800 hover:bg-amber-50'
            : 'border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50'
        }`}>
        <Settings className="h-3 w-3" />
        {reportOnly ? 'الرجوع للوضع التلقائي' : 'تفعيل الوضع المُقيَّد'}
      </button>
    </div>
  );
}

const ExpensesReport = () => {
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  // P15-EXP1 — fetched on mount; used to flag unmapped categories before
  // the accountant submits the report so they can fix mappings up-front.
  const [categoryMappings, setCategoryMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'saved'
  const [selectedReport, setSelectedReport] = useState(null);
  const [previewImage, setPreviewImage] = useState(null); // For image preview modal
  // P14-C5 · Classification filter for the Saved Reports tab.
  // Values: 'all' | 'PROJECT' | 'ADMINISTRATIVE' | 'MIXED'
  const [classificationFilter, setClassificationFilter] = useState('all');
  // P14-C5 · Track in-flight repost so we can disable the button + spin the icon.
  const [repostingId, setRepostingId] = useState(null);
  
  // Filters - default to last 12 months
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    category: '',
    project_id: ''
  });

  const categories = [
    'Materials', 'Labor', 'Equipment', 'Transportation', 
    'Utilities', 'Rent', 'Insurance', 'Maintenance', 
    'Office Supplies', 'Professional Services', 'Other'
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a4de6c', '#d0ed57', '#8dd1e1'];

  // P15-EXP1 · Journal → Report deep link. When the user clicks
  // "View Expense Report" from the Journals page, we land here with
  // ``?focus=<report_id>``. Switch to the Saved tab and auto-expand
  // the matching card.
  const [searchParams] = useSearchParams();
  const focusReportId = searchParams.get('focus');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!focusReportId || savedReports.length === 0) return;
    const target = savedReports.find(r => r.id === focusReportId);
    if (target) {
      setActiveTab('saved');
      setSelectedReport(target);
      // Smooth scroll into view for visual confirmation.
      setTimeout(() => {
        const node = document.querySelector(`[data-testid="report-card-${target.id}"]`);
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
  }, [focusReportId, savedReports]);

  const fetchData = async () => {
    try {
      const [expensesRes, accountsRes, projectsRes, reportsRes, mappingsRes] = await Promise.all([
        axiosInstance.get('/expenses'),
        axiosInstance.get('/accounts'),
        axiosInstance.get('/projects'),
        axiosInstance.get('/expense-reports'),
        axiosInstance.get('/expense-category-mappings').catch(() => ({ data: [] }))
      ]);
      setExpenses(expensesRes.data);
      setAccounts(accountsRes.data);
      setProjects(projectsRes.data);
      setSavedReports(reportsRes.data);
      setCategoryMappings(mappingsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Delete report
  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/expense-reports/${reportId}`);
      toast.success('Report deleted successfully');
      fetchData(); // Refresh the list
      setSelectedReport(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete report');
    }
  };

  // P14-C5 · Reverse + Re-post an approved report through the current
  // routing rules. Original journal is NEVER deleted — a reversal entry
  // is posted alongside a fresh corrected entry (full audit trail).
  const handleRepostReport = async (report) => {
    const confirmMsg =
      `Reverse and re-post ${report.report_no}?\n\n` +
      `This will:\n` +
      `  1. Post a REVERSAL of the current journal (${report.journal_no || 'n/a'})\n` +
      `  2. Re-build the journal with current Project/Administrative routing\n` +
      `  3. Link the new journal back to this report\n\n` +
      `The original journal is preserved as a reversal entry. Continue?`;
    if (!window.confirm(confirmMsg)) return;

    setRepostingId(report.id);
    try {
      const { data } = await axiosInstance.post(`/expense-reports/${report.id}/repost`);
      toast.success(
        `${data.report_no} reposted as ${data.new_journal_no} ` +
        `(${data.classification}, ${data.line_count_new} debit line(s)).`,
        { duration: 6000 },
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repost report');
    } finally {
      setRepostingId(null);
    }
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const expenseDate = new Date(expense.expense_date);
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    
    let match = expenseDate >= startDate && expenseDate <= endDate;
    
    if (filters.category && expense.expense_type !== filters.category) {
      match = false;
    }
    
    if (filters.project_id && expense.project_id !== filters.project_id) {
      match = false;
    }
    
    return match;
  });

  // Selection handlers
  const toggleSelectExpense = (expenseId) => {
    setSelectedExpenses(prev => 
      prev.includes(expenseId) 
        ? prev.filter(id => id !== expenseId)
        : [...prev, expenseId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedExpenses.length === filteredExpenses.length) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(filteredExpenses.map(e => e.id));
    }
  };

  const isAllSelected = filteredExpenses.length > 0 && selectedExpenses.length === filteredExpenses.length;

  // Get selected expenses data
  const selectedExpensesData = filteredExpenses.filter(e => selectedExpenses.includes(e.id));

  // Calculate statistics (for selected if any, otherwise all filtered)
  const expensesForStats = selectedExpenses.length > 0 ? selectedExpensesData : filteredExpenses;
  const stats = {
    total: expensesForStats.reduce((sum, e) => sum + (e.total_with_vat || e.amount || 0), 0),
    count: expensesForStats.length,
    avgPerExpense: expensesForStats.length > 0 
      ? expensesForStats.reduce((sum, e) => sum + (e.total_with_vat || e.amount || 0), 0) / expensesForStats.length 
      : 0,
    selectedCount: selectedExpenses.length
  };

  // Group by category
  const expensesByCategory = categories.map(cat => ({
    name: cat,
    value: expensesForStats.filter(e => e.expense_type === cat).reduce((sum, e) => sum + (e.total_with_vat || e.amount || 0), 0)
  })).filter(item => item.value > 0);

  // Group by month
  const expensesByMonth = {};
  expensesForStats.forEach(expense => {
    // Defensive: legacy rows may have null/missing expense_date.
    // Without this guard a single bad row crashes the whole report
    // page with a blank screen ("Cannot read property substring of null").
    const raw = expense?.expense_date;
    if (!raw || typeof raw !== 'string' || raw.length < 7) return;
    const month = raw.substring(0, 7); // YYYY-MM
    if (!expensesByMonth[month]) {
      expensesByMonth[month] = 0;
    }
    expensesByMonth[month] += (expense.total_with_vat || expense.amount || 0);
  });

  const monthlyData = Object.entries(expensesByMonth)
    .map(([month, amount]) => ({ month, amount }))
    .sort((a, b) => a.month.localeCompare(b.month));

  // Group by project
  const expensesByProject = {};
  expensesForStats.forEach(expense => {
    const projectName = expense.project_name || 'No Project';
    if (!expensesByProject[projectName]) {
      expensesByProject[projectName] = 0;
    }
    expensesByProject[projectName] += (expense.total_with_vat || expense.amount || 0);
  });

  const projectData = Object.entries(expensesByProject)
    .map(([name, amount]) => ({ name, amount }))
    .sort((a, b) => b.amount - a.amount);

  // Download PDF Report

  // Direct ADD - Submit for approval (no journal/archive/PDF until approved)
  const handleAdd = async () => {
    const expensesToExport = selectedExpenses.length > 0 ? selectedExpensesData : filteredExpenses;
    
    if (expensesToExport.length === 0) {
      toast.error('No expenses to export');
      return;
    }

    // Feb 2026 · Cash-Expenses routing policy
    // ---------------------------------------------------------------
    // Prior bug: the picker took ``assetAccountsList[0]`` which
    // happened to be ``11000 Current Assets`` (a header account, not
    // a leaf) — every cash-paid expense report credited the wrong
    // account.  The corrected policy resolves the cash side by
    // priority:
    //   1. system_code === 'CASH_EXPENSES'   (canonical marker)
    //   2. account_number === '11150'         (numeric fallback)
    //   3. is_cash_account === true           (any cash leaf)
    //   4. first non-header asset account     (legacy fallback)
    // The expense (DR) side follows the same discipline: prefer a
    // real expense/COGS account, never a header.
    const _byPriority = (list, tests) => {
      for (const t of tests) {
        const hit = list.find(t);
        if (hit) return hit;
      }
      return null;
    };

    const cashAccount = _byPriority(accounts, [
      a => (a.system_code || '').toUpperCase() === 'CASH_EXPENSES',
      a => a.account_number === '11150',
      a => a.is_cash_account === true,
      a => (a.account_type === 'assets')
           && !(a.account_number || '').endsWith('000')
           && !(a.parent_account_id === null || a.parent_account_id === undefined),
    ]);

    const expenseAccount = _byPriority(accounts, [
      a => a.account_type === 'expenses'
           && !(a.account_number || '').endsWith('000'),
      a => a.account_type === 'expenses',
    ]);

    if (!expenseAccount || !cashAccount) {
      toast.error('Please create Expense and Asset accounts first');
      return;
    }

    const totalAmount = expensesToExport.reduce((sum, e) => sum + (e.total_with_vat || e.amount || 0), 0);
    const journalReference = `EXP-${Date.now()}`;

    try {
      // Save expense report for approval (pending status)
      // P0 (Feb 2026) — Recorded-date-not-creation-date policy:
      // The report's journal_date MUST fall within the reported period,
      // not on "today".  Otherwise a Q1 expense report keyed in July
      // rolls the aggregated journal into July and pollutes Q3 GL /
      // VAT reconciliation.  We use the LATEST ``expense_date`` in the
      // batch (capped to ``period_end``) — this puts the journal
      // exactly on the last real economic date of the batch.
      const _maxExpDate = expensesToExport
        .map(e => (e.expense_date || '').slice(0, 10))
        .filter(Boolean)
        .sort()
        .pop() || filters.endDate;
      const _bookedOn =
        _maxExpDate && filters.endDate && _maxExpDate > filters.endDate
          ? filters.endDate
          : (_maxExpDate || filters.endDate || new Date().toISOString().split('T')[0]);

      const reportData = {
        report_date: _bookedOn,
        period_start: filters.startDate,
        period_end: filters.endDate,
        total_amount: totalAmount,
        expenses_count: expensesToExport.length,
        debit_account_id: expenseAccount.id,
        debit_account_name: expenseAccount.account_name,
        credit_account_id: cashAccount.id,
        credit_account_name: cashAccount.account_name,
        journal_reference: journalReference,
        expense_ids: expensesToExport.map(e => e.id), // IDs to archive after approval
        expenses: expensesToExport.map(e => ({
          expense_date: e.expense_date,
          expense_from: e.expense_from,
          expense_no: e.expense_no,
          expense_type: e.expense_type,
          description: e.description,
          amount: e.amount,
          vat: e.vat,
          total_with_vat: e.total_with_vat,
          project_name: e.project_name,
          photo_url: e.photo_url || null,  // Include invoice photo
          supplier_vat_number: e.supplier_vat_number || null,  // Carry VAT number for ZATCA VAT Return
          supplier_id: e.supplier_id || null,
          id: e.id  // Keep original expense id for fallback lookups
        }))
      };
      
      await axiosInstance.post('/expense-reports', reportData);
      
      toast.success(`Report submitted for approval! ${expensesToExport.length} expenses included. Check Approvals page.`);
      setSelectedExpenses([]);
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error('Export error:', error.response?.data);
      // Detail may be a dict from 409s — coerce to string so the toast
      // (and any downstream React node) never receives an object.
      const detail = error?.response?.data?.detail;
      let msg = 'Failed to create report';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (detail && typeof detail === 'object') {
        msg = detail.message
            || detail.error
            || JSON.stringify(detail);
      } else if (error?.message) {
        msg = error.message;
      }
      toast.error(msg);
    }
  };

  // Download PDF for approved report
  const downloadReportPDF = (report) => {
    const doc = new jsPDF();
    
    // Add Arabic font for Arabic content only
    doc.addFileToVFS('NotoSansArabic.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic.ttf', 'NotoSansArabic', 'normal');
    
    const formatArabicText = (text) => {
      if (!text) return '';
      const hasArabic = /[\u0600-\u06FF]/.test(text);
      return hasArabic ? convertArabic(text) : text;
    };
    
    // Header - English only
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(10, 66, 117);
    doc.text('Expenses Report', 105, 20, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Report No: ${report.report_no}`, 105, 28, { align: 'center' });
    doc.text(`Period: ${report.period_start} to ${report.period_end}`, 105, 34, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, 40, { align: 'center' });

    // Summary Box
    doc.setFillColor(240, 247, 255);
    doc.roundedRect(14, 48, 182, 30, 2, 2, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Amount: ${Number(report.total_amount || 0).toLocaleString()} SAR`, 20, 58);
    doc.text(`Number of Expenses: ${report.expenses_count}`, 20, 66);
    doc.text(`Debit Account: ${report.debit_account_name || 'N/A'}`, 110, 58);
    doc.text(`Credit Account: ${report.credit_account_name || 'N/A'}`, 110, 66);

    // Expenses Table - English headers
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text('Expense Details', 14, 90);

    autoTable(doc, {
      startY: 94,
      head: [['Date', 'Description', 'Type', 'Project', 'Vendor', 'VAT No.', 'Total With VAT']],
      body: (report.expenses || []).map(e => [
        e.expense_date,
        (e.description || '').substring(0, 30),
        e.expense_type || '-',
        e.project_name || '-',
        e.expense_from || '-',
        e.supplier_vat_number || '-',
        `${(e.total_with_vat || e.amount || 0).toLocaleString()} SAR`
      ]),
      theme: 'grid',
      headStyles: { fillColor: [10, 66, 117], fontSize: 9, font: 'helvetica', fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 40 },
        2: { cellWidth: 22 },
        3: { cellWidth: 25 },
        4: { cellWidth: 30 },
        5: { cellWidth: 25 },
        6: { cellWidth: 22, halign: 'right' }
      },
      didParseCell: function(data) {
        if (data.section === 'body') {
          const cellText = String(data.cell.raw || '');
          const hasArabic = /[\u0600-\u06FF]/.test(cellText);
          if (hasArabic) {
            data.cell.styles.font = 'NotoSansArabic';
            // Apply Arabic text formatting
            data.cell.text = [formatArabicText(cellText)];
          } else {
            data.cell.styles.font = 'helvetica';
          }
        }
      }
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFillColor(10, 66, 117);
    doc.roundedRect(14, finalY, 182, 20, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`Total: ${Number(report.total_amount || 0).toLocaleString()} SAR`, 105, finalY + 12, { align: 'center' });

    doc.save(`${report.report_no}_Expenses_Report.pdf`);
    toast.success('PDF downloaded successfully!');
  };

  // Generate and download PDF (for approved reports)
  const generatePDF = (expensesToExport, totalAmount, expenseAccount, cashAccount) => {
    const doc = new jsPDF();
    
    // Add Arabic font
    doc.addFileToVFS('NotoSansArabic.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic.ttf', 'NotoSansArabic', 'normal');
    
    // Helper function for Arabic text
    const formatText = (text) => {
      if (!text) return '';
      const hasArabic = /[\u0600-\u06FF]/.test(text);
      return hasArabic ? convertArabic(text) : text;
    };
    
    // Header
    doc.setFont('NotoSansArabic');
    doc.setFontSize(18);
    doc.setTextColor(10, 66, 117);
    doc.text(formatText('تقرير المصروفات - Expenses Report'), 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Period: ${filters.startDate} to ${filters.endDate}`, 105, 28, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, 105, 34, { align: 'center' });
    doc.text(`${expensesToExport.length} expenses exported to Journal`, 105, 40, { align: 'center' });

    // Summary Box
    doc.setFillColor(240, 247, 255);
    doc.roundedRect(14, 48, 182, 30, 2, 2, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Amount: ${totalAmount.toLocaleString()} SAR`, 20, 58);
    doc.text(`Number of Expenses: ${expensesToExport.length}`, 20, 66);
    doc.text(`Debit Account: ${formatText(expenseAccount?.account_name || 'N/A')}`, 110, 58);
    doc.text(`Credit Account: ${formatText(cashAccount?.account_name || 'N/A')}`, 110, 66);

    // Expenses Table
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text(formatText('تفاصيل المصروفات - Expense Details'), 14, 90);

    autoTable(doc, {
      startY: 94,
      head: [['Date', formatText('الوصف'), formatText('النوع'), formatText('المشروع'), formatText('المورد'), formatText('الرقم الضريبي'), formatText('المبلغ شامل الضريبة')]],
      body: expensesToExport.map(e => [
        e.expense_date,
        (e.description || '').substring(0, 25),
        e.expense_type || '',
        e.project_name || '-',
        e.expense_from || '-',
        e.supplier_vat_number || '-',
        `${(e.total_with_vat || e.amount || 0).toLocaleString()} SAR`
      ]),
      theme: 'grid',
      headStyles: { fillColor: [10, 66, 117], fontSize: 8, font: 'NotoSansArabic' },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        6: { halign: 'right' }
      },
      margin: { left: 14, right: 14 },
      didParseCell: function(data) {
        if (data.section === 'body') {
          const cellText = String(data.cell.raw || '');
          const hasArabic = /[\u0600-\u06FF]/.test(cellText);
          if (hasArabic) {
            data.cell.styles.font = 'NotoSansArabic';
            data.cell.text = [formatText(cellText)];
          } else {
            data.cell.styles.font = 'helvetica';
          }
        }
      }
    });

    // Total row
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 5 : 150;
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(14, finalY, 182, 12, 2, 2, 'F');
    doc.setFontSize(11);
    doc.setFont('NotoSansArabic', 'normal');
    doc.setTextColor(10, 66, 117);
    doc.text(formatText('الإجمالي - Total:'), 150, finalY + 8);
    doc.text(`${totalAmount.toLocaleString()} SAR`, 192, finalY + 8, { align: 'right' });

    // Journal Entry Info
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('This expense report has been recorded in Journal.', 105, finalY + 25, { align: 'center' });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Distinct High Road - Accounting System', 105, 285, { align: 'center' });

    // Download PDF directly
    doc.save('Expenses_Report.pdf');
    toast.success('PDF downloaded');
  };

  // Get expense and asset accounts
  const expenseAccounts = accounts.filter(a => a.account_type === 'expenses');
  const assetAccounts = accounts.filter(a => a.account_type === 'assets' || a.is_bank_account);

  if (loading) {
    return <div className="text-center py-12">Loading report...</div>;
  }

  return (
    <div data-testid="expenses-report-page">
      <PostModeBanner />
      <div className="page-header">
        <h2 className="page-title">Expenses Report</h2>
        <div className="flex gap-2">
          <button 
            onClick={() => setActiveTab('new')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'new' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            New Report
          </button>
          <button 
            onClick={() => setActiveTab('saved')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'saved' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Saved Reports ({savedReports.length})
          </button>
        </div>
      </div>

      {activeTab === 'saved' ? (
        /* Saved Reports Tab */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Saved Reports for Review &amp; Audit</h3>
            {/* P14-C5 · Classification filter pills */}
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" data-testid="classification-filter">
              {[
                { key: 'all',            label: 'All' },
                { key: 'PROJECT',        label: '🏗️ Project' },
                { key: 'ADMINISTRATIVE', label: '🏢 Administrative' },
                { key: 'MIXED',          label: '🔀 Mixed' },
              ].map(opt => (
                <button
                  key={opt.key}
                  data-testid={`classification-filter-${opt.key.toLowerCase()}`}
                  onClick={() => setClassificationFilter(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
                    classificationFilter === opt.key
                      ? 'bg-white shadow-sm text-gray-900 ring-1 ring-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            const filtered = classificationFilter === 'all'
              ? savedReports
              : savedReports.filter(r => (r.classification || 'ADMINISTRATIVE') === classificationFilter);
            return filtered.length === 0 ? (
              <p className="text-gray-500 text-center py-8" data-testid="no-reports-msg">
                {classificationFilter === 'all'
                  ? 'No reports yet. Submit a report for approval using the ADD button.'
                  : `No ${classificationFilter.toLowerCase()} reports.`}
              </p>
            ) : (
            <div className="space-y-4">
              {filtered.map(report => (
                <div 
                  key={report.id}
                  data-testid={`report-card-${report.id}`}
                  className={`border rounded-xl p-4 cursor-pointer transition-all ${
                    selectedReport?.id === report.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  } ${focusReportId === report.id ? 'ring-2 ring-emerald-300' : ''}`}
                  onClick={() => setSelectedReport(selectedReport?.id === report.id ? null : report)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900">{report.report_no}</h4>
                        {/* P14-C5 · Classification badge */}
                        {(() => {
                          const cls = (report.classification || 'ADMINISTRATIVE').toUpperCase();
                          const cfg = cls === 'PROJECT'
                            ? { cls: 'bg-indigo-100 text-indigo-700', txt: '🏗️ Project' }
                            : cls === 'MIXED'
                              ? { cls: 'bg-purple-100 text-purple-700', txt: '🔀 Mixed' }
                              : { cls: 'bg-slate-100 text-slate-700',   txt: '🏢 Administrative' };
                          return (
                            <span
                              data-testid={`report-classification-${report.id}`}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.cls}`}
                              title={`Classification: ${cls}`}
                            >
                              {cfg.txt}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-sm text-gray-500">
                        Period: {report.period_start} to {report.period_end}
                      </p>
                      <p className="text-sm text-gray-500">
                        Created: {new Date(report.created_at).toLocaleDateString()} by {report.user_name}
                      </p>
                      {(() => {
                        const st = (report.approval_status || 'pending').toLowerCase();
                        const cfg = st === 'approved' ? { cls: 'bg-green-100 text-green-700',  txt: '✓ Approved' }
                                  : st === 'rejected' ? { cls: 'bg-red-100 text-red-700',      txt: '✕ Rejected' }
                                  :                     { cls: 'bg-amber-100 text-amber-700',  txt: '⏳ Pending Approval' };
                        return (
                          <span
                            data-testid={`report-status-${report.id}`}
                            className={`inline-flex items-center px-2 py-1 mt-1 rounded-full text-xs font-medium ${cfg.cls}`}
                          >
                            {cfg.txt}
                          </span>
                        );
                      })()}
                      {(report.approval_status || 'pending').toLowerCase() === 'pending' && (
                        <a
                          href="/approvals"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`goto-approvals-${report.id}`}
                          className="ml-2 inline-flex items-center px-2 py-1 mt-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                          title="This report is awaiting admin approval. No journal will be posted until approved."
                        >
                          → Open Approvals
                        </a>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-blue-600">{Number(report.total_amount || 0).toLocaleString()} SAR</p>
                      <p className="text-sm text-gray-500">{report.expenses_count} expenses</p>
                      {/* P15-EXP1 · Report → Journal back-navigation.
                          When the new ``journal_no`` field is populated
                          (post-P15-EXP1 approvals), surface a clickable
                          link to the Journals page focused on that entry.
                          Legacy approvals fall back to the read-only
                          ``journal_reference`` display. */}
                      {report.journal_no ? (
                        <a
                          href={`/journal?focus=${report.journal_id || report.journal_no}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`open-journal-${report.id}`}
                          className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline font-mono"
                          title="Open the source journal entry"
                        >
                          Journal: {report.journal_no} →
                        </a>
                      ) : (
                        <p className="text-xs text-gray-400">Journal: {report.journal_reference}</p>
                      )}
                      <div className="flex gap-2 mt-2 justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadReportPDF(report);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> PDF
                        </button>
                        {/* P14-C5 · Reverse + Re-post (Admin only · approved reports only) */}
                        {(report.approval_status || '').toLowerCase() === 'approved' && (
                          <button
                            data-testid={`repost-btn-${report.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRepostReport(report);
                            }}
                            disabled={repostingId === report.id}
                            title="Reverse the existing journal and re-post it with the current Project/Administrative routing rules. The original journal is preserved as a reversal entry."
                            className="px-3 py-1 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 flex items-center gap-1 disabled:opacity-50"
                          >
                            <RefreshCw className={`w-4 h-4 ${repostingId === report.id ? 'animate-spin' : ''}`} />
                            {repostingId === report.id ? 'Reposting…' : 'Reverse + Re-post'}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteReport(report.id);
                          }}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-1"
                        >
                          <X className="w-4 h-4" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {selectedReport?.id === report.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-xs text-green-600 font-medium">Debit Account</p>
                          <p className="font-semibold text-green-700">{report.debit_account_name}</p>
                        </div>
                        <div className="bg-red-50 p-3 rounded-lg">
                          <p className="text-xs text-red-600 font-medium">Credit Account</p>
                          <p className="font-semibold text-red-700">{report.credit_account_name}</p>
                        </div>
                      </div>
                      
                      <h5 className="font-semibold text-gray-700 mb-2">Expense Details:</h5>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-2">Date</th>
                              <th className="text-left p-2">Vendor</th>
                              <th className="text-left p-2">VAT Number<br/><span className="font-normal text-[10px] text-gray-500">الرقم الضريبي</span></th>
                              <th className="text-left p-2">Type</th>
                              <th className="text-left p-2">Description</th>
                              <th className="text-right p-2">Total With VAT</th>
                              <th className="text-center p-2">Invoice</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(report.expenses || []).map((exp, idx) => (
                              <tr key={idx} className="border-t border-gray-100">
                                <td className="p-2">{exp.expense_date || '—'}</td>
                                <td className="p-2">{exp.expense_from || '—'}</td>
                                <td className="p-2 font-mono text-xs text-gray-700">{exp.supplier_vat_number || <span className="text-gray-400">—</span>}</td>
                                <td className="p-2">{exp.expense_type || '—'}</td>
                                <td className="p-2">{exp.description || '—'}</td>
                                <td className="p-2 text-right font-medium">{Number(exp.total_with_vat || exp.amount || 0).toLocaleString()} SAR</td>
                                <td className="p-2 text-center">
                                  {exp.photo_url && typeof exp.photo_url === 'string' ? (
                                    exp.photo_url.toLowerCase().endsWith('.pdf') ? (
                                      <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}${exp.photo_url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="flex items-center justify-center gap-1 text-red-600 hover:text-red-800 mx-auto"
                                        title="View PDF Invoice"
                                      >
                                        <FileText className="w-5 h-5" />
                                        <span className="text-xs">PDF</span>
                                      </a>
                                    ) : (
                                      <div 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPreviewImage(`${process.env.REACT_APP_BACKEND_URL}${exp.photo_url}`);
                                        }}
                                        className="cursor-pointer hover:opacity-80 transition-opacity"
                                        title="Click to enlarge"
                                      >
                                        <img 
                                          src={`${process.env.REACT_APP_BACKEND_URL}${exp.photo_url}`}
                                          alt="Invoice"
                                          className="w-10 h-10 object-cover rounded border border-gray-200 mx-auto hover:border-blue-400"
                                        />
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
          })()}
        </div>
      ) : (
        /* New Report Tab */
        <>
          <div className="flex justify-end mb-4">
            <button 
              onClick={handleAdd} 
              className="btn-primary"
              disabled={filteredExpenses.length === 0}
            >
              <Plus className="w-5 h-5 inline mr-2" />
              ADD
            </button>
          </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-5 h-5 text-gray-500" />
          <h3 className="font-semibold text-gray-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="form-label">From Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">To Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="form-input"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Project</label>
            <select
              value={filters.project_id}
              onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
              className="form-input"
            >
              <option value="">All Projects</option>
              {projects.map(proj => (
                <option key={proj.id} value={proj.id}>{proj.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* P15-EXP1 — Unmapped Categories warning. Renders only when the
          user has selected expenses whose ``expense_type`` lacks a row
          in the per-tenant Category Mapping. We render this BEFORE the
          table so the accountant sees the gap before submitting. */}
      {(() => {
        const target = selectedExpenses.length > 0
          ? expenses.filter(e => selectedExpenses.includes(e.id))
          : filteredExpenses;
        if (target.length === 0) return null;
        const mappedSet = new Set(
          (categoryMappings || []).map(m => (m.category_normalized || (m.category || '').toLowerCase().trim()))
        );
        const unmappedCats = new Set();
        target.forEach(e => {
          const cat = (e.expense_type || '').trim();
          if (cat && !mappedSet.has(cat.toLowerCase())) unmappedCats.add(cat);
        });
        if (unmappedCats.size === 0) return null;
        return (
          <div
            data-testid="unmapped-categories-warning"
            className="mb-4 border border-amber-200 bg-amber-50 rounded-lg p-4 flex items-start gap-3"
          >
            <div className="text-amber-600 mt-0.5">⚠</div>
            <div className="flex-1">
              <div className="font-semibold text-amber-900 text-sm">
                Unmapped Categories Detected ({unmappedCats.size})
              </div>
              <div className="text-xs text-amber-800 mt-1">
                These expense categories have no GL account mapping. They will fall back
                to the report&apos;s default debit account when the report is approved, and
                will be flagged on the resulting journal.
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {[...unmappedCats].map(c => (
                  <span key={c} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-white border border-amber-300 text-amber-900 font-mono">
                    {c}
                  </span>
                ))}
              </div>
              <a
                href="/expense-category-mappings"
                className="inline-flex items-center mt-3 text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                data-testid="go-to-mappings-link"
              >
                Go to Category Mappings →
              </a>
            </div>
          </div>
        );
      })()}

      {/* Expense Details Table - Directly after Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h3 className="font-semibold text-gray-800">Expense Details</h3>
            <span className="text-sm text-gray-500">
              ({filteredExpenses.length} items | Total: {stats.total.toLocaleString()} SAR)
            </span>
          </div>
          {selectedExpenses.length > 0 && (
            <button
              onClick={() => setSelectedExpenses([])}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Clear Selection ({selectedExpenses.length})
            </button>
          )}
        </div>
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-center">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-600 hover:text-blue-600"
                  title={isAllSelected ? "Deselect All" : "Select All"}
                >
                  {isAllSelected ? (
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Project</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vendor</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">VAT Number<br/><span className="normal-case font-normal text-[10px] text-gray-500">الرقم الضريبي</span></th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Total With VAT</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredExpenses.length > 0 ? (
              filteredExpenses.map((expense) => (
                <tr 
                  key={expense.id} 
                  className={`hover:bg-gray-50 cursor-pointer ${selectedExpenses.includes(expense.id) ? 'bg-blue-50' : ''}`}
                  onClick={() => toggleSelectExpense(expense.id)}
                >
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleSelectExpense(expense.id)}
                      className="text-gray-600 hover:text-blue-600"
                    >
                      {selectedExpenses.includes(expense.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{expense.expense_date}</td>
                  <td className="px-4 py-3 text-gray-900">{expense.description}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                      {expense.expense_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{expense.project_name || '-'}</td>
                  <td className="px-4 py-3 text-gray-600">{expense.expense_from || '-'}</td>
                  <td className="px-4 py-3 text-gray-700 font-mono text-xs">{expense.supplier_vat_number || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-600">
                    {(expense.total_with_vat || expense.amount || 0).toLocaleString()} SAR
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                  No expenses found for the selected period
                </td>
              </tr>
            )}
          </tbody>
          {filteredExpenses.length > 0 && (
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="7" className="px-4 py-3 text-right font-bold text-gray-700">
                  {selectedExpenses.length > 0 ? `Selected Total (${selectedExpenses.length}):` : 'Total:'}
                </td>
                <td className="px-4 py-3 text-right font-bold text-red-600">
                  {stats.total.toLocaleString()} SAR
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon red">
            <TrendingDown className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">
            {selectedExpenses.length > 0 ? 'Selected Total' : 'Total Expenses'}
          </h3>
          <p className="text-3xl font-bold text-red-600">{stats.total.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue">
            <Calendar className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">
            {selectedExpenses.length > 0 ? 'Selected Count' : 'Number of Expenses'}
          </h3>
          <p className="text-3xl font-bold text-gray-900">{stats.count}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <TrendingDown className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Average per Expense</h3>
          <p className="text-3xl font-bold text-purple-600">{stats.avgPerExpense.toFixed(2)} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <CheckSquare className="w-6 h-6" />
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Selected</h3>
          <p className="text-3xl font-bold text-green-600">
            {selectedExpenses.length} / {filteredExpenses.length}
          </p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* By Category Pie Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Expenses by Category</h3>
          {expensesByCategory.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={expensesByCategory}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value.toLocaleString()} SAR`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Monthly Bar Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Monthly Expenses</h3>
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${value.toLocaleString()} SAR`} />
                <Bar dataKey="amount" fill="#ef4444" name="Expenses" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* By Project */}
      {projectData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">Expenses by Project</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={projectData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip formatter={(value) => `${value.toLocaleString()} SAR`} />
              <Bar dataKey="amount" fill="#8884d8" name="Amount" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
        </>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 flex items-center gap-2"
            >
              <X className="w-6 h-6" />
              <span>Close</span>
            </button>
            <img 
              src={previewImage}
              alt="Invoice Preview"
              className="w-full h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-3">
              <a
                href={previewImage}
                download
                onClick={(e) => e.stopPropagation()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(previewImage, '_blank');
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Open in New Tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesReport;
